import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./QueuePanel.module.scss";
import classNames from "classnames";
import { TicketStatus } from "../../../types/enums";
import { useQueueItems } from "../../../hooks/MonitorQueue/useQueueItems";
import SoundPlayer from "./SoundPlayer";
import { useUpdateSoundStatus } from "../../../hooks/MonitorQueue/useUpdateSoundStatus";
import ModalQueueNewTicket from "../Modals/ModalQueueNewTicket/ModalQueueNewTicket";
import WeatherForecast from "./Weather/WeatherForecast";
import {
  isQueueScreenDarkTheme,
  isQueueTitleEnabled,
  isQueueWeatherForecastEnabled,
} from "../../../constants/API";
import { useGetQueueHeaders } from "../../../hooks/MonitorQueue/useGetQueueHeaders";
import { QueueTicketIcon } from "../../../assets/svgComponents/QueueTicketIcon";
import { QueueWindowIcon } from "../../../assets/svgComponents/QueueWindowIcon";
import { TerminalTabloSettings } from "../../../types/Admin/TerminalAppearanceTypes";
import { useGetCreatedTickets } from "../../../hooks/MonitorQueue/useGetCreatedTickets";
import { QueueTicketClook } from "../../../assets/svgComponents/QueueTicketClook";
import { QueueTicketItem } from "./QueueTicketItem";

export type MonitorUnitType = {
  itemId: number;
  itemName: string;
  window: string;
  status: string;
  audioList: string[];
  isSoundPlayed: boolean;
};

export type MonitorsUnitType = {
  items: MonitorUnitType[];
};

export type ReceivedSoundArrType = {
  sounds: string[][];
  receivedSoundsIds: number[];
};

type QueuePanelProps = {
  tabloSettings?: TerminalTabloSettings | null;
  layoutMode?: boolean;
  onSoundPlayingChange?: (playing: boolean) => void;
  onVideoFullScreenChange?: (fullScreen: boolean) => void;
  onVideoHostChange?: (host: HTMLElement | null) => void;
};

export const QueuePanel = ({
  tabloSettings,
  layoutMode,
  onSoundPlayingChange,
  onVideoFullScreenChange,
  onVideoHostChange,
}: QueuePanelProps) => {
  const [receivedSoundArr, setReceivedSoundArr] =
    useState<ReceivedSoundArrType>();
  const [isSoundPlaying, setIsSoundPlaying] = useState<boolean>(false);
  const { queueItemsResp, isQueueItemsRecieved, clearSoundsSignature } = useQueueItems(
    setReceivedSoundArr,
    isSoundPlaying,
  );
  const { queueHeadersResp } = useGetQueueHeaders();
  const { updateSoundStatus } = useUpdateSoundStatus();
  const titleElement = tabloSettings?.elements?.title;
  const queueElement = tabloSettings?.elements?.queue;
  const takenTicketsElement = tabloSettings?.elements?.takenTickets;
  const videoElement = tabloSettings?.elements?.video;
  const isTakenTicketsEnabled =
    !!layoutMode && (takenTicketsElement?.isEnabled ?? false);
  const { createdTicketsResp } = useGetCreatedTickets(isTakenTicketsEnabled);
  const [isModalActive, setIsModalActive] = useState(false);
  const isTitleEnabled =
    (titleElement?.isEnabled ?? true) && isQueueTitleEnabled;
  const shouldRenderTitle = isTitleEnabled || isQueueWeatherForecastEnabled;
  const isQueueEnabled = queueElement?.isEnabled ?? true;
  const queueTwoColumns = queueElement?.queueTwoColumns ?? false;
  const queueItems = useMemo(() => {
    const raw = queueItemsResp?.items ?? [];
    if (raw.length <= 1) return raw;
    return [...raw].sort((a, b) => b.itemId - a.itemId);
  }, [queueItemsResp?.items]);
  const queueLeftBodyRef = useRef<HTMLDivElement>(null);
  const queueFirstItemRef = useRef<HTMLDivElement>(null);
  const [queueSplitIndex, setQueueSplitIndex] = useState(0);

  const updateQueueSplitIndex = useCallback(() => {
    const bodyEl = queueLeftBodyRef.current;
    const itemEl = queueFirstItemRef.current;
    if (!queueTwoColumns || queueItems.length === 0 || !bodyEl || !itemEl)
      return;
    const columnEl = bodyEl.parentElement;
    const headerEl = bodyEl.previousElementSibling as HTMLElement | null;
    const style = getComputedStyle(bodyEl);
    const cssMaxHeight = parseFloat(style.maxHeight) || 840;
    const gap = parseFloat(style.gap) || 10;
    const itemHeight = itemEl.offsetHeight;
    if (itemHeight <= 0) return;
    const availableHeight =
      columnEl && headerEl
        ? columnEl.clientHeight - headerEl.offsetHeight
        : cssMaxHeight;
    const heightCap = Math.min(cssMaxHeight, Math.max(0, availableHeight));
    const countThatFits = Math.max(
      0,
      Math.floor((heightCap + gap) / (itemHeight + gap)),
    );
    setQueueSplitIndex((prev) => {
      const next = Math.min(countThatFits, queueItems.length);
      return next === prev ? prev : next;
    });
  }, [queueTwoColumns, queueItems.length]);

  useLayoutEffect(() => {
    if (!queueTwoColumns || queueItems.length === 0) {
      setQueueSplitIndex(0);
      return;
    }
    updateQueueSplitIndex();
    const bodyEl = queueLeftBodyRef.current;
    const columnEl = bodyEl?.parentElement;
    if (!columnEl) return;
    const ro = new ResizeObserver(updateQueueSplitIndex);
    ro.observe(columnEl);
    return () => ro.disconnect();
  }, [queueTwoColumns, queueItems.length, updateQueueSplitIndex]);

  const isVideoEnabled = videoElement?.isEnabled ?? true;
  const hasQueueTickets = queueItems.length > 0;
  const hasCreatedTickets = (createdTicketsResp?.items?.length ?? 0) > 0;
  const hasAnyTickets = hasQueueTickets || hasCreatedTickets;
  const videoFullScreenWhenNoTickets =
    !!layoutMode &&
    !!videoElement?.videoFullScreenWhenNoTickets &&
    !hasAnyTickets;

  useEffect(() => {
    onSoundPlayingChange?.(isSoundPlaying);
  }, [isSoundPlaying, onSoundPlayingChange]);

  useEffect(() => {
    onVideoFullScreenChange?.(videoFullScreenWhenNoTickets);
  }, [videoFullScreenWhenNoTickets, onVideoFullScreenChange]);

  useEffect(() => {
    return () => onVideoHostChange?.(null);
  }, [onVideoHostChange]);

  const takenTicketsItems = useMemo(() => {
    if (!isTakenTicketsEnabled) return [];
    const raw = createdTicketsResp?.items?.slice(0, 20) ?? [];
    const sortOldToNew = takenTicketsElement?.takenTicketsSortOldToNew ?? false;
    if (raw.length <= 1) return raw;
    return [...raw].sort((a, b) =>
      sortOldToNew ? a.itemId - b.itemId : b.itemId - a.itemId,
    );
  }, [
    isTakenTicketsEnabled,
    createdTicketsResp?.items,
    takenTicketsElement?.takenTicketsSortOldToNew,
  ]);
  const titleTextStyles = useMemo<CSSProperties>(() => {
    const baseFontSize = titleElement?.titleFontSize ?? titleElement?.fontSize;
    return {
      fontFamily:
        titleElement?.titleFontFamily || titleElement?.fontFamily || undefined,
      fontWeight:
        titleElement?.titleFontWeight || titleElement?.fontWeight || undefined,
      fontSize: baseFontSize ? `${baseFontSize}px` : undefined,
      color:
        titleElement?.titleTextColor || titleElement?.textColor || undefined,
      lineHeight: titleElement?.titleLineHeight
        ? `${titleElement.titleLineHeight}px`
        : undefined,
    };
  }, [
    titleElement?.titleFontFamily,
    titleElement?.fontFamily,
    titleElement?.titleFontWeight,
    titleElement?.fontWeight,
    titleElement?.titleFontSize,
    titleElement?.fontSize,
    titleElement?.titleTextColor,
    titleElement?.textColor,
    titleElement?.titleLineHeight,
  ]);
  const subTitleTextStyles = useMemo<CSSProperties>(() => {
    const baseFontSize =
      titleElement?.subTitleFontSize ??
      (titleElement?.fontSize
        ? Math.max(10, Math.round(titleElement.fontSize * 0.65))
        : undefined);
    return {
      fontFamily:
        titleElement?.subTitleFontFamily ||
        titleElement?.fontFamily ||
        undefined,
      fontWeight:
        titleElement?.subTitleFontWeight ||
        titleElement?.fontWeight ||
        undefined,
      fontSize: baseFontSize ? `${baseFontSize}px` : undefined,
      color:
        titleElement?.subTitleTextColor || titleElement?.textColor || undefined,
      lineHeight: titleElement?.subTitleLineHeight
        ? `${titleElement.subTitleLineHeight}px`
        : undefined,
    };
  }, [
    titleElement?.subTitleFontFamily,
    titleElement?.fontFamily,
    titleElement?.subTitleFontWeight,
    titleElement?.fontWeight,
    titleElement?.subTitleFontSize,
    titleElement?.fontSize,
    titleElement?.subTitleTextColor,
    titleElement?.textColor,
    titleElement?.subTitleLineHeight,
  ]);
  const queueBaseFont = queueElement?.fontSize ?? null;
  const queueTextStyles = useMemo<CSSProperties>(() => {
    return {
      fontFamily: queueElement?.fontFamily || undefined,
      fontWeight: queueElement?.fontWeight || undefined,
      color: queueElement?.textColor || undefined,
    };
  }, [
    queueElement?.fontFamily,
    queueElement?.fontWeight,
    queueElement?.textColor,
  ]);
  const takenTicketsTextStyles = useMemo<CSSProperties>(() => {
    return {
      fontFamily: takenTicketsElement?.fontFamily || undefined,
      fontWeight: takenTicketsElement?.fontWeight || undefined,
      color: takenTicketsElement?.textColor || undefined,
      fontSize: takenTicketsElement?.fontSize
        ? `${takenTicketsElement.fontSize}px`
        : undefined,
    };
  }, [
    takenTicketsElement?.fontFamily,
    takenTicketsElement?.fontWeight,
    takenTicketsElement?.textColor,
    takenTicketsElement?.fontSize,
  ]);
  const takenTicketsHeaderColor =
    takenTicketsElement?.textColor || queueElement?.textColor || undefined;
  const queueHeaderFontSize = queueBaseFont
    ? `${Math.max(10, Math.round(queueBaseFont * 0.6))}px`
    : undefined;
  const ticketFontSize = queueBaseFont
    ? `${Math.max(12, Math.round(queueBaseFont * 1.1))}px`
    : undefined;
  const windowFontLarge = queueBaseFont
    ? `${Math.max(12, Math.round(queueBaseFont * 1))}px`
    : undefined;
  const windowFontMiddle = queueBaseFont
    ? `${Math.max(12, Math.round(queueBaseFont * 0.9))}px`
    : undefined;
  const windowFontSmall = queueBaseFont
    ? `${Math.max(12, Math.round(queueBaseFont * 0.8))}px`
    : undefined;
  const queueNumberBoxStyles = useMemo<CSSProperties>(() => {
    const widthPx = Number(queueElement?.numberWidth);
    const heightPx = Number(queueElement?.numberHeight);
    const resolvedWidth =
      Number.isFinite(widthPx) && widthPx > 0 ? `${widthPx}px` : undefined;
    const resolvedHeight =
      Number.isFinite(heightPx) && heightPx > 0 ? `${heightPx}px` : undefined;
    return {
      width: resolvedWidth,
      minWidth: 0,
      maxWidth: "100%",
      height: resolvedHeight,
      minHeight: resolvedHeight,
      maxHeight: resolvedHeight,
    };
  }, [queueElement?.numberHeight, queueElement?.numberWidth]);
  const takenTicketNumberSizeStyles = useMemo<CSSProperties>(() => {
    const widthPx = Number(takenTicketsElement?.numberWidth);
    const heightPx = Number(takenTicketsElement?.numberHeight);
    const resolvedWidth =
      Number.isFinite(widthPx) && widthPx > 0 ? `${widthPx}px` : undefined;
    const resolvedHeight =
      Number.isFinite(heightPx) && heightPx > 0 ? `${heightPx}px` : undefined;
    return {
      width: resolvedWidth,
      minWidth: 0,
      maxWidth: "100%",
      height: resolvedHeight,
      minHeight: resolvedHeight,
      maxHeight: resolvedHeight,
    };
  }, [takenTicketsElement?.numberHeight, takenTicketsElement?.numberWidth]);

  const videoHostStyle = useMemo(() => {
    if (!videoElement) return undefined;
    return {
      backgroundColor: videoElement.backgroundColor || undefined,
    };
  }, [videoElement]);

  const buildElementStyle = useCallback(
    (element?: TerminalTabloSettings["elements"]["video"] | null) => {
      if (!element) {
        return undefined;
      }
      const x = Number.isFinite(Number(element.x)) ? element.x : 0;
      const y = Number.isFinite(Number(element.y)) ? element.y : 0;
      const w = Number.isFinite(Number(element.width)) ? element.width : 60;
      const h = Number.isFinite(Number(element.height)) ? element.height : 55;
      const positionStyle: CSSProperties = layoutMode
        ? {
            position: "absolute",
            left: `${x}%`,
            top: `${y}%`,
            width: `${w}%`,
            height: `${h}%`,
            zIndex: 1,
          }
        : {};
      return {
        ...positionStyle,
        backgroundColor: element.backgroundColor || undefined,
      };
    },
    [layoutMode],
  );

  const titleContainerStyle = useMemo(
    () => buildElementStyle(titleElement),
    [buildElementStyle, titleElement],
  );
  const queueContainerStyle = useMemo(
    () => buildElementStyle(queueElement),
    [buildElementStyle, queueElement],
  );
  const takenTicketsContainerStyle = useMemo(
    () => buildElementStyle(takenTicketsElement),
    [buildElementStyle, takenTicketsElement],
  );

  const callingTicketsSignature = useMemo(() => {
    if (!queueItemsResp?.items?.length) return "";
    return queueItemsResp.items
      .filter((e) => e.status !== TicketStatus.TakeInWorking)
      .map((e) => e.itemId)
      .join(",");
  }, [queueItemsResp?.items]);

  useEffect(() => {
    setIsModalActive(!!callingTicketsSignature);
  }, [callingTicketsSignature]);

  const queueTicketColorsStyle = useMemo(() => {
    if (!queueElement) return undefined;
    const style: CSSProperties = {};
    if (queueElement.ticketBGColor) {
      (style as Record<string, string>)["--queueScreen-ticketBG"] =
        queueElement.ticketBGColor;
    }
    if (queueElement.ticketBlinkBGColor) {
      (style as Record<string, string>)["--queueScreen-ticketBGgreen"] =
        queueElement.ticketBlinkBGColor;
    }
    return Object.keys(style).length ? style : undefined;
  }, [queueElement?.ticketBGColor, queueElement?.ticketBlinkBGColor]);

  const ticketItemProps = {
    queueTextStyles,
    queueNumberBoxStyles,
    ticketFontSize,
    windowFontLarge,
    windowFontMiddle,
    windowFontSmall,
  };

  return (
    <div
      className={classNames(styles.queuePanelContainer, {
        [styles.darkQueuePanelContainer]: isQueueScreenDarkTheme,
        [styles.layoutMode]: layoutMode,
      })}
      style={queueTicketColorsStyle}
    >
      {isQueueItemsRecieved && (
        <SoundPlayer
          updateSoundStatus={updateSoundStatus}
          soundsData={receivedSoundArr}
          setIsSoundPlaying={setIsSoundPlaying}
          onPlayFailed={clearSoundsSignature}
        />
      )}
      {isModalActive && queueItemsResp?.items && isSoundPlaying && (
        <ModalQueueNewTicket tickets={queueItemsResp} />
      )}
      {isVideoEnabled && (
        <div
          className={classNames(styles.videoContainer, {
            [styles.videoContainerFullScreen]: videoFullScreenWhenNoTickets,
            [styles.videoContainerNoTitle]: !layoutMode && !shouldRenderTitle,
          })}
          style={
            layoutMode
              ? { display: "none" }
              : videoHostStyle
          }
          ref={(node) => {
            if (!layoutMode) {
              onVideoHostChange?.(node);
            }
          }}
        >
          <div className={styles.videoBox} />
        </div>
      )}
      {shouldRenderTitle && (
        <div
          className={classNames(styles.titleContainer, {
            [styles.isTitleActive]:
              isQueueTitleEnabled && !isQueueScreenDarkTheme,
            [styles.isWeatherForecastActive]: isQueueWeatherForecastEnabled,
            [styles.darkThemeTitleContainer]: isQueueScreenDarkTheme,
            [styles.layoutItem]: layoutMode,
          })}
          style={titleContainerStyle}
        >
          {isTitleEnabled && queueHeadersResp && (
            <div className={styles.titleWrapper}>
              <p
                className={classNames(styles.title, {
                  [styles.darkThemeText]: isQueueScreenDarkTheme,
                })}
                style={titleTextStyles}
              >
                {queueHeadersResp.title}
              </p>
              <p
                className={classNames(styles.subTitle, {
                  [styles.darkThemeText]: isQueueScreenDarkTheme,
                })}
                style={subTitleTextStyles}
              >
                {queueHeadersResp.subTitle}
              </p>
            </div>
          )}
          {isQueueWeatherForecastEnabled && <WeatherForecast />}
        </div>
      )}
      {isQueueEnabled && (
        <div
          className={classNames(styles.queueContainer, {
            [styles.queueContainerTwoColumns]: queueTwoColumns,
            [styles.darkQueueContainer]: isQueueScreenDarkTheme,
            [styles.layoutItem]: layoutMode,
          })}
          style={queueContainerStyle}
        >
          {queueTwoColumns ? (
            <div className={styles.queueTwoColumnsInner}>
              {(() => {
                const items = queueItems;
                const effectiveSplit =
                  queueSplitIndex === 0 && items.length > 0
                    ? 1
                    : queueSplitIndex;
                const leftItems = items.slice(0, effectiveSplit);
                const rightItems = items.slice(effectiveSplit);
                const renderColumn = (
                  columnItems: MonitorUnitType[],
                  columnKey: string,
                  bodyRef?: React.RefObject<HTMLDivElement | null>,
                  firstItemRef?: React.RefObject<HTMLDivElement | null>,
                ) => (
                  <div className={styles.queueColumn} key={columnKey}>
                    <div
                      className={classNames(styles.queueContainerHeader, {
                        [styles.darkQueueContainerHeader]:
                          isQueueScreenDarkTheme,
                      })}
                    >
                      <div className={styles.queueHeaderTitlesWrapper}>
                        <div className={styles.queueHeaderTitle}>
                          <QueueTicketIcon color={queueElement?.textColor} />
                          <span
                            className={styles.queueHeaderText}
                            style={
                              queueHeaderFontSize
                                ? {
                                    ...queueTextStyles,
                                    fontSize: queueHeaderFontSize,
                                  }
                                : queueTextStyles
                            }
                          >
                            Талон
                          </span>
                        </div>
                        <div className={styles.queueHeaderTitle}>
                          <QueueWindowIcon color={queueElement?.textColor} />
                          <span
                            className={styles.queueHeaderText}
                            style={
                              queueHeaderFontSize
                                ? {
                                    ...queueTextStyles,
                                    fontSize: queueHeaderFontSize,
                                  }
                                : queueTextStyles
                            }
                          >
                            Окно
                          </span>
                        </div>
                      </div>
                      {isQueueScreenDarkTheme && (
                        <div className={styles.queueHeaderSeparateLine} />
                      )}
                    </div>
                    <div ref={bodyRef} className={styles.queueContainerBody}>
                      {columnItems.map(
                        (item: MonitorUnitType, index: number) => (
                          <QueueTicketItem
                            key={item.itemId}
                            item={item}
                            itemRef={
                              firstItemRef && index === 0
                                ? firstItemRef
                                : undefined
                            }
                            {...ticketItemProps}
                          />
                        ),
                      )}
                    </div>
                  </div>
                );
                return (
                  <>
                    {renderColumn(
                      leftItems,
                      "left",
                      queueLeftBodyRef,
                      queueFirstItemRef,
                    )}
                    <div className={styles.queueColumnDivider} />
                    {renderColumn(rightItems, "right")}
                  </>
                );
              })()}
            </div>
          ) : (
            <>
              <div
                className={classNames(styles.queueContainerHeader, {
                  [styles.darkQueueContainerHeader]: isQueueScreenDarkTheme,
                })}
              >
                <div className={styles.queueHeaderTitlesWrapper}>
                  <div className={styles.queueHeaderTitle}>
                    <QueueTicketIcon color={queueElement?.textColor} />
                    <span
                      className={styles.queueHeaderText}
                      style={
                        queueHeaderFontSize
                          ? {
                              ...queueTextStyles,
                              fontSize: queueHeaderFontSize,
                            }
                          : queueTextStyles
                      }
                    >
                      {/* Талон */}
                    </span>
                  </div>
                  <div className={styles.queueHeaderTitle}>
                    <QueueWindowIcon color={queueElement?.textColor} />
                    <span
                      className={styles.queueHeaderText}
                      style={
                        queueHeaderFontSize
                          ? {
                              ...queueTextStyles,
                              fontSize: queueHeaderFontSize,
                            }
                          : queueTextStyles
                      }
                    >
                      {/* Окно */}
                    </span>
                  </div>
                </div>
                {isQueueScreenDarkTheme && (
                  <div className={styles.queueHeaderSeparateLine} />
                )}
              </div>
              <div className={styles.queueContainerBody}>
                {queueItems.map((item: MonitorUnitType) => (
                  <QueueTicketItem
                    key={item.itemId}
                    item={item}
                    {...ticketItemProps}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
      {isTakenTicketsEnabled && (
        <div
          className={classNames(styles.takenTicketsContainer, {
            [styles.darkQueueContainer]: isQueueScreenDarkTheme,
            [styles.layoutItem]: layoutMode,
          })}
          style={takenTicketsContainerStyle}
        >
          <div
            className={classNames(
              styles.queueContainerHeader,
              styles.takenTicketsHeader,
              {
                [styles.darkQueueContainerHeader]: isQueueScreenDarkTheme,
              },
            )}
          >
            <div
              className={classNames(
                styles.queueHeaderTitlesWrapper,
                styles.takenTicketsHeaderTitlesWrapper,
              )}
            >
              <div className={styles.queueHeaderTitle}>
                <QueueTicketClook color={takenTicketsHeaderColor} />
                <span
                  className={styles.queueHeaderText}
                  style={
                    queueHeaderFontSize
                      ? {
                          ...queueTextStyles,
                          color: takenTicketsHeaderColor,
                          fontSize: queueHeaderFontSize,
                        }
                      : { ...queueTextStyles, color: takenTicketsHeaderColor }
                  }
                >
                  {/* Очередь */}
                </span>
              </div>
            </div>

            {isQueueScreenDarkTheme && (
              <div className={styles.queueHeaderSeparateLine} />
            )}
          </div>
          <div className={styles.takenTicketsList}>
            {takenTicketsItems.map((item, index) => (
              <div
                key={item.itemId}
                className={classNames(styles.takenTicketItem, {
                  [styles.takenTicketItemActive]: index === 0,
                })}
                style={{
                  ...takenTicketsTextStyles,
                  ...takenTicketNumberSizeStyles,
                }}
              >
                {item.itemName}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default QueuePanel;
